import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSeoSchema } from '@/hooks/useSeoSchema';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useSeoSchema(
    'schema-contact',
    [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'İletişim - Health Travel Türkiye',
        description: 'Contact Health Travel Türkiye.',
        url: `${SITE_URL}/iletisim`,
      },
    ],
    [{ nameKey: 'breadcrumb_contact', url: `${SITE_URL}/iletisim` }],
    i18n.language,
  );

  useEffect(() => {
    const tc = i18n.getFixedT(i18n.language, 'common');
    document.title = tc('seo_contact_title');
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', tc('seo_contact_desc'));
    setMeta('keywords', 'contact, support, health travel turkey contact, health platform support');
    setMeta('og:title', tc('seo_contact_title'), true);
    setMeta('og:description', tc('seo_contact_desc'), true);
    setMeta('og:url', `${SITE_URL}/iletisim`, true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/debd8e71bc5df412f1977b04fb94aad8.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=customer%20support%20contact%20center%20friendly%20professional%20team%20health%20tourism%20platform%20modern%20office%20warm%20welcoming%20atmosphere&width=1200&height=630&seq=og-contact-v1&orientation=landscape');
  }, [i18n.language]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6amvult1k61917cmtfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const subjects = [
    { value: 'General Information', key: 'contact_subject_general' },
    { value: 'Institution Registration', key: 'contact_subject_institution' },
    { value: 'Technical Support', key: 'contact_subject_technical' },
    { value: 'Suggestion', key: 'contact_subject_suggestion' },
    { value: 'Complaint', key: 'contact_subject_complaint' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-['Outfit']">
      <Navbar />

      <div style={{ paddingTop: 'var(--navbar-height, 160px)' }}>
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">{t('contact_title')}</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              {t('contact_subtitle')}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact_form_title')}</h2>
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact_name')} {t('contact_required')}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-emerald-600 text-sm"
                        placeholder={t('contact_name_placeholder')}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact_email')} {t('contact_required')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-emerald-600 text-sm"
                        placeholder={t('contact_email_placeholder')}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact_phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-emerald-600 text-sm"
                        placeholder={t('contact_phone_placeholder')}
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact_subject')} {t('contact_required')}
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-emerald-600 text-sm cursor-pointer"
                      >
                        <option value="">{t('contact_subject_placeholder')}</option>
                        {subjects.map((s) => (
                          <option key={s.value} value={s.value}>{t(s.key)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact_message')} {t('contact_required')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-emerald-600 text-sm resize-none"
                      placeholder={t('contact_message_placeholder')}
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('contact_char_count', { count: formData.message.length })}
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-sm text-emerald-700 flex items-center">
                        <i className="ri-check-line mr-2"></i>
                        {t('contact_success')}
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 flex items-center">
                        <i className="ri-error-warning-line mr-2"></i>
                        {t('contact_error')}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? t('contact_submitting') : t('contact_submit')}
                  </button>
                </form>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-md p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('contact_info_title')}</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg">
                      <i className="ri-mail-line text-emerald-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('contact_email_label')}</p>
                      <p className="text-sm font-medium text-gray-900">healthtravelturkey@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg">
                      <i className="ri-map-pin-line text-emerald-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('contact_address_label')}</p>
                      <p className="text-sm font-medium text-gray-900">Istanbul, Turkey</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
